import {
    BsdfPrincipled,
    Color,
    isColor,
    isVec3,
    MaterialConnection,
    MaterialNode,
    TextureFromRevision,
    TextureFromSet,
    TextureSet,
    TextureType,
} from "@cm/material-nodes"
import type {MaterialGraph} from "@cm/material-nodes"
import {isSupportedLinearImageOperatorSequenceNode} from "#material-maps-exporter/core/types"

export class MaterialGraphUnsupportedError extends Error {
    readonly nodeClass: string | undefined
    /** Populated on the refusal raised for the BSDF slots as a whole; empty on a throw from a single site. */
    readonly issues: MaterialGraphIssue[]

    constructor(message: string, nodeClass?: string, issues: MaterialGraphIssue[] = []) {
        super(message)
        this.name = "MaterialGraphUnsupportedError"
        this.nodeClass = nodeClass
        this.issues = issues
    }
}

/** A graph the exporter cannot even walk: an invariant violation, not something an author did. */
export class MaterialGraphInvariantError extends MaterialGraphUnsupportedError {}

/**
 * `shape` is decidable from the graph alone — the editor can check it live. `data` only surfaces once
 * the referenced texture sets and revisions are resolved, so it needs the server.
 */
export type MaterialGraphIssueCategory = "shape" | "data"

export type MaterialGraphIssue = {
    bsdfInput: string
    /** The same slot as the author reads it on the BSDF, so every consumer names it one way. */
    bsdfInputLabel: string
    nodeClass: string | undefined
    category: MaterialGraphIssueCategory
    message: string
    /** Kept in the export's refusal, hidden from the editor's warnings — the author cannot act on it. */
    internal?: boolean
}

const nodeLabel = (node: MaterialNode): string => node.definition.ui?.label ?? node.nodeClass

const inputSocketLabel = (node: MaterialNode, socketName: string): string => {
    const item = node.definition.ui?.items.find((item) => !item.custom && item.name === socketName && item.input)
    return item !== undefined && !item.custom ? item.label : socketName
}

/** The label the author reads on the BSDF's socket, so a warning and a refusal name the slot the same way. */
export const bsdfInputLabel = (bsdf: BsdfPrincipled, bsdfInput: string): string => inputSocketLabel(bsdf, bsdfInput)

const connectedInputLabelsOtherThanColor = (node: MaterialNode): string[] =>
    Object.keys(node.parameters.inputs ?? {})
        .filter((socketName) => socketName !== "color")
        .map((socketName) => inputSocketLabel(node, socketName))

const joinAnd = (labels: string[]): string => (labels.length <= 1 ? (labels[0] ?? "") : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`)

export type BsdfInput = "alpha" | "baseColor" | "metallic" | "roughness" | "specular"

/** The BSDF inputs the exporter reads, each with the texture type it feeds, in the order slots are reported. */
export const BSDF_INPUT_TEXTURE_TYPES: readonly (readonly [BsdfInput, TextureType])[] = [
    ["baseColor", TextureType.Diffuse],
    ["metallic", TextureType.Metalness],
    ["specular", TextureType.SpecularStrength],
    ["roughness", TextureType.Roughness],
    ["alpha", TextureType.Mask],
]

/** The node a slot's chain starts from: a texture reference to resolve, or a constant colour. */
export type SlotHead = TextureFromRevision | TextureSet | TextureFromSet | Color

export const isSlotHead = (node: MaterialNode): node is SlotHead =>
    node instanceof TextureFromRevision || node instanceof TextureSet || node instanceof TextureFromSet || node instanceof Color

export type SlotAnalysis =
    /** Neither a connection nor a setting — an expected miss, which the gather's backstop may still fill. */
    | {kind: "unconnected"}
    /** A linear chain the exporter can read: its head, then the transforms applied to it in order. */
    | {kind: "chain"; head: SlotHead; transformations: MaterialNode[]}
    | {kind: "unsupported"; issue: MaterialGraphIssue}

export type SlotAnalysisEntry = {bsdfInput: BsdfInput; textureType: TextureType; analysis: SlotAnalysis}

export type MaterialGraphAnalysis = {kind: "noBsdf"; bsdfCount: number} | {kind: "bsdf"; bsdf: BsdfPrincipled; slots: SlotAnalysisEntry[]}

/**
 * Why a chain head cannot be resolved as it stands, or `undefined` when its settings are complete.
 * Shared with the resolvers so Phase 1 and Phase 2 refuse an unconfigured texture node with one message.
 */
export const incompleteHeadMessage = (node: SlotHead): string | undefined => {
    if (node instanceof TextureFromRevision) {
        return node.parameters.settings.textureRevisionLegacyId ? undefined : `${nodeLabel(node)}: no texture is selected.`
    }
    if (node instanceof TextureFromSet && !node.mapAssignmentTextureType) {
        return `${nodeLabel(node)}: no map is selected.`
    }
    if (node instanceof TextureSet || node instanceof TextureFromSet) {
        return node.parameters.settings.textureSetRevisionId ? undefined : `${nodeLabel(node)}: no texture set is selected.`
    }
    return undefined
}

const parameterToShaderNodeRGB = (parameter: unknown): Color => {
    if (parameter instanceof Color) return parameter
    else if (isColor(parameter)) return new Color({parameters: {inputs: {}, settings: {color: parameter}}})
    else if (typeof parameter === "number") {
        return new Color({
            parameters: {
                inputs: {},
                settings: {
                    color: {r: parameter, g: parameter, b: parameter},
                },
            },
        })
    } else if (isVec3(parameter)) {
        return new Color({
            parameters: {
                inputs: {},
                settings: {
                    color: {r: parameter.x, g: parameter.y, b: parameter.z},
                },
            },
        })
    } else throw new MaterialGraphUnsupportedError("The value set here is neither a number nor a colour, so the exporter cannot read it.")
}

const collectLinearImageOperatorSequence = (node: unknown) => {
    const linearTransforms: MaterialNode[] = []

    const traverse = (node: unknown) => {
        if (node instanceof MaterialConnection) {
            traverse(node.source)
            return
        }
        if (!(node instanceof MaterialNode)) throw new MaterialGraphInvariantError(`Encountered a graph entry that is not a material node`)
        if (!isSupportedLinearImageOperatorSequenceNode(node))
            throw new MaterialGraphUnsupportedError(`${nodeLabel(node)} is not a node the exporter can read.`, node.nodeClass)
        linearTransforms.push(node)
        if (node instanceof TextureFromRevision || node instanceof TextureSet || node instanceof TextureFromSet) return
        if (node.parameters.inputs && Object.keys(node.parameters.inputs).length > 0) {
            const otherInputLabels = connectedInputLabelsOtherThanColor(node)
            const otherInputs = joinAnd(otherInputLabels)
            if (Object.keys(node.parameters.inputs).length !== 1)
                throw new MaterialGraphUnsupportedError(
                    `${nodeLabel(node)}: the exporter follows only the ${inputSocketLabel(node, "color")} input. ${otherInputs} ${
                        otherInputLabels.length > 1 ? "are" : "is"
                    } also connected.`,
                    node.nodeClass,
                )
            // Only a transform reaches this: `Color` declares no inputs at all, and the texture heads return above.
            if (!("color" in node.parameters.inputs))
                throw new MaterialGraphUnsupportedError(
                    `${nodeLabel(node)}: the exporter follows the chain through the ${inputSocketLabel(node, "color")} input. Here only ${otherInputs} is connected.`,
                    node.nodeClass,
                )
            traverse(node.parameters.inputs.color)
        } else if (node instanceof Color) {
            // Just guard check, already pushed above
            if (node.parameters.settings.color === undefined)
                throw new MaterialGraphUnsupportedError(`${nodeLabel(node)}: no colour is set on this node.`, node.nodeClass)
        }
    }

    traverse(node)
    return linearTransforms.reverse()
}

const getBsdfLinearImageOperatorSequence = (bsdfNode: BsdfPrincipled, bsdfInput: BsdfInput): MaterialNode[] | undefined => {
    const inputNode = bsdfNode.parameters.inputs?.[bsdfInput]
    if (inputNode === undefined) {
        const parameter = bsdfNode.parameters.settings?.[bsdfInput]
        if (parameter !== undefined) return [parameterToShaderNodeRGB(parameter)]
        else return undefined
    } else return collectLinearImageOperatorSequence(inputNode)
}

const analyzeSlot = (bsdfNode: BsdfPrincipled, bsdfInput: BsdfInput): SlotAnalysis => {
    try {
        const sequence = getBsdfLinearImageOperatorSequence(bsdfNode, bsdfInput)
        if (sequence === undefined) return {kind: "unconnected"}
        const head = sequence[0]
        if (head === undefined) throw new MaterialGraphUnsupportedError(`BSDF input ${bsdfInput} does not have a linear image operator sequence`)
        if (!isSlotHead(head)) {
            throw new MaterialGraphUnsupportedError(
                `${nodeLabel(head)} has nothing connected to it. A chain must start at an image or a colour node.`,
                head.nodeClass,
            )
        }
        const incomplete = incompleteHeadMessage(head)
        if (incomplete !== undefined) throw new MaterialGraphUnsupportedError(incomplete, head.nodeClass)

        const transformations = sequence.slice(1)
        const misplacedNode = transformations.find(isSlotHead)
        if (misplacedNode !== undefined)
            throw new MaterialGraphUnsupportedError(
                `BSDF input ${bsdfInput} has a ${misplacedNode.nodeClass} node after the one feeding the chain, so the chain is not linear`,
                misplacedNode.nodeClass,
            )
        return {kind: "chain", head, transformations}
    } catch (error) {
        if (!(error instanceof MaterialGraphUnsupportedError)) throw error
        return {
            kind: "unsupported",
            issue: {
                bsdfInput,
                bsdfInputLabel: bsdfInputLabel(bsdfNode, bsdfInput),
                nodeClass: error.nodeClass,
                category: "shape",
                message: error.message,
                ...(error instanceof MaterialGraphInvariantError ? {internal: true} : {}),
            },
        }
    }
}

/**
 * What the exporter can read from a material graph, decided from the graph alone — no texture or
 * revision is resolved. Each BSDF slot is either unconnected, a linear chain from a texture or colour
 * node, or unsupported with the reason. The gather resolves the chains; the editor can show the
 * result live.
 */
export function analyzeMaterialGraph(graph: MaterialGraph): MaterialGraphAnalysis {
    const bsdfNodes = graph.nodes.filter((node) => node instanceof BsdfPrincipled)
    if (bsdfNodes.length !== 1) return {kind: "noBsdf", bsdfCount: bsdfNodes.length}
    const bsdf = bsdfNodes[0]

    return {
        kind: "bsdf",
        bsdf,
        slots: BSDF_INPUT_TEXTURE_TYPES.map(([bsdfInput, textureType]) => ({bsdfInput, textureType, analysis: analyzeSlot(bsdf, bsdfInput)})),
    }
}

/** Why the exporter refuses the graph as a whole, independent of any slot. */
export const noBsdfMessage = (analysis: Extract<MaterialGraphAnalysis, {kind: "noBsdf"}>): string =>
    `The exporter needs exactly one Principled BSDF node; this material has ${analysis.bsdfCount === 0 ? "none" : analysis.bsdfCount}.`

/**
 * The slots the exporter cannot read, as an author can act on them. An unconnected slot is not one of
 * them — it is an expected miss — and an invariant violation is not either.
 */
export const unreadableSlotIssues = (analysis: MaterialGraphAnalysis): MaterialGraphIssue[] =>
    analysis.kind === "noBsdf" ? [] : analysis.slots.flatMap(({analysis: slot}) => (slot.kind === "unsupported" && !slot.issue.internal ? [slot.issue] : []))
